import React from "react";
import { motion } from "framer-motion";

const LandingPage = () => {
  const REACT_URL = process.env.REACT_APP_URL || "";

  const goToGoogleAuth = (plan = null) => {
    if (!REACT_URL) {
      console.error("REACT_URL is not configured");
      alert("App misconfigured. Please try again later.");
      return;
    }

    const query = plan ? `?plan=${encodeURIComponent(plan)}` : "";

    window.location.href = `${REACT_URL}/gauth${query}`;
  };

  return (
    <div className="font-sans text-gray-900">
      {/* ================= HEADER ================= */}
      <header className="w-full bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="text-2xl font-bold text-gray-900">VlogSlicer</div>

          <button
            onClick={() => goToGoogleAuth()}
            className="text-blue-600 font-medium hover:underline"
          >
            Log in
          </button>
        </div>
      </header>

      {/* ================= HERO ================= */}
      <section className="bg-gradient-to-b from-gray-100 to-white py-24 text-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <h1 className="text-5xl font-bold mb-6 leading-tight">
            Turn Blogs & Long Videos <br />
            <span className="text-blue-600">Into Viral Short Content</span>
          </h1>

          <p className="text-xl text-gray-600 mb-10">
            BlogSlicer helps creators repurpose blogs and long videos into
            Twitter threads, LinkedIn posts, and short-form videos — in minutes.
          </p>

          <div className="flex justify-center gap-4 flex-wrap">
            <button
              onClick={() => goToGoogleAuth()}
              className="bg-blue-600 text-white px-8 py-4 rounded-full font-medium text-lg hover:bg-blue-500 transition"
            >
              Start Free with Google
            </button>

            <button
              onClick={() => goToGoogleAuth()}
              className="border border-blue-600 text-blue-600 px-8 py-4 rounded-full font-medium text-lg hover:bg-blue-50 transition"
            >
              Log in
            </button>
          </div>

          <p className="mt-4 text-sm text-gray-500">
            No credit card required · Sign up in seconds
          </p>
        </motion.div>
      </section>

      {/* ================= SOCIAL PROOF ================= */}
      <section className="py-10 text-center text-gray-500 text-sm">
        Trusted by indie creators, bloggers & video makers
      </section>

      {/* ================= FEATURES ================= */}
      <section className="py-24 px-6 max-w-6xl mx-auto">
        <h2 className="text-3xl font-bold text-center mb-16">
          Everything you need to repurpose content
        </h2>

        <div className="grid md:grid-cols-3 gap-12">
          {[
            {
              title: "🧵 Blog → Tweets / Threads",
              desc: "Paste a blog URL and instantly generate clean, readable Twitter threads or LinkedIn posts.",
            },
            {
              title: "🎬 Long Video → Shorts",
              desc: "Turn long videos into short, shareable clips for YouTube Shorts, Reels, and TikTok.",
            },
            {
              title: "🎯 Hashtags & Tone Boost",
              desc: "Automatically enhance reach with smart hashtags and tone customization.",
            },
            {
              title: "⚡ Fast & Simple",
              desc: "No learning curve. Generate, edit, and export in minutes.",
            },
            {
              title: "📂 Content History",
              desc: "Access all your generated content anytime in one place.",
            },
            {
              title: "🔐 Google Login",
              desc: "Secure sign-in with Google. No passwords to remember.",
            },
          ].map((f, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.08 }}
              className="bg-white rounded-xl p-6 shadow-sm border"
            >
              <h3 className="text-xl font-semibold mb-3">{f.title}</h3>
              <p className="text-gray-600">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= PRICING ================= */}
      <section id="pricing" className="bg-gray-50 py-24 px-6">
        <h2 className="text-3xl font-bold text-center mb-14">Simple pricing</h2>

        <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
          {[
            {
              title: "Free",
              price: "$0",
              subtitle: "Try before you upgrade",
              features: [
                "Limited blog & video conversions",
                "Access to core features",
                "Content history",
              ],
              cta: "Start Free",
              plan: null,
            },
            {
              title: "Starter",
              price: "$10 / month",
              highlight: true,
              features: [
                "Unlimited blog → tweets",
                "Unlimited video → shorts",
                "Hashtag booster",
              ],
              cta: "Choose Starter",
              plan: "starter",
            },
            {
              title: "Pro",
              price: "$20 / month",
              features: [
                "Everything in Starter",
                "Tone customization",
                "Export-ready content",
              ],
              cta: "Choose Pro",
              plan: "pro",
            },
          ].map((p, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.1 }}
              className={`rounded-xl p-8 text-center border bg-white ${
                p.highlight ? "border-blue-600 shadow-xl" : ""
              }`}
            >
              <h3 className="text-xl font-bold mb-2">{p.title}</h3>
              <p className="text-3xl font-bold mb-2">{p.price}</p>

              {p.subtitle && (
                <p className="text-sm text-gray-500 mb-4">{p.subtitle}</p>
              )}

              <ul className="mb-8 space-y-2 text-gray-600">
                {p.features.map((f, idx) => (
                  <li key={idx}>✓ {f}</li>
                ))}
              </ul>

              <button
                onClick={() => goToGoogleAuth(p.plan)}
                className="bg-blue-600 text-white px-6 py-3 rounded-full font-medium hover:bg-blue-500 transition"
              >
                {p.cta}
              </button>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= FOOTER ================= */}
      <footer className="text-center text-sm py-8 bg-gray-100">
        <p className="mb-3">
          © {new Date().getFullYear()} BlogSlicer. All rights reserved.
        </p>
        <div className="space-x-4">
          <a href="/termsandconditions" className="underline">
            Terms
          </a>
          <a href="/termsandconditions" className="underline">
            Privacy
          </a>
          <a href="/termsandconditions" className="underline">
            Refunds
          </a>
        </div>
      </footer>
    </div>
  );
};

export default LandingPage;
